import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { encryptPassword } from '../utils/crypto';

const ResetPasswordPage = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const token = location.state?.token;

    // If no token is present, we shouldn't be here
    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <div className="w-full max-w-md bg-white shadow-sm rounded-2xl p-8 border border-slate-200 text-center">
                    <p className="text-red-500 mb-4">Invalid password reset session.</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full rounded-lg bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const encryptedNew = encryptPassword(newPassword);
            const encryptedConfirm = encryptPassword(confirmPassword);

            await api.post(
                '/api/auth/reset-password',
                { newPassword: encryptedNew, confirmPassword: encryptedConfirm },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // On success, redirect to login so they can log in normally with the new password
            navigate('/login', { replace: true });
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-md bg-white shadow-sm rounded-2xl p-8 border border-slate-200">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-900">Reset Password</h1>
                    <p className="text-sm text-slate-500 mt-1">Please set a new password to continue.</p>
                </div>

                <form className="space-y-4" onSubmit={handleResetPassword}>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                        <input
                            type="password"
                            required
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="••••••••"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                        <input
                            type="password"
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="••••••••"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full rounded-lg bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-60"
                    >
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
